// The Composer's STEP MODEL — the 8-step operator rail (Brief → … → Activate) translated from the
// approved prototype (docs/design/work-hifi/work.js composerView/STEPS/STEP_HEAD; brief §7 v2). This is
// PRESENTATION structure only: every configuration VALUE the steps render comes from the
// WorkComposerProjection the owning seam serves (G-182) — nothing here invents config. The step copy is
// the prototype's reviewed wording; the activation heading derives from the drafted workflow's name.

/** The stable step identifiers, in rail order. */
export type ComposerStepId =
  | 'brief'
  | 'skill'
  | 'bindings'
  | 'lifecycle'
  | 'actions'
  | 'view'
  | 'approvers'
  | 'activate';

/** One rail step: its two-digit label, rail title/subtitle, and whether it is the activation gate. */
export interface ComposerStep {
  readonly id: ComposerStepId;
  readonly number: string;
  readonly railTitle: string;
  readonly railSub: string;
  readonly gate: boolean;
}

const BRIEF_STEP: ComposerStep = {
  id: 'brief',
  number: '01',
  railTitle: 'Brief',
  railSub: 'The owner’s ask',
  gate: false,
};

/** The ordered rail (prototype STEPS). The last step is the ONE human gate — activation. */
export const COMPOSER_STEPS: readonly ComposerStep[] = [
  BRIEF_STEP,
  {
    id: 'skill',
    number: '02',
    railTitle: 'Skill',
    railSub: 'Goal · limits · playbook',
    gate: false,
  },
  { id: 'bindings', number: '03', railTitle: 'Bindings', railSub: 'Declared pulls', gate: false },
  { id: 'lifecycle', number: '04', railTitle: 'Lifecycle', railSub: 'States · exits', gate: false },
  {
    id: 'actions',
    number: '05',
    railTitle: 'Action palette',
    railSub: 'What it may do',
    gate: false,
  },
  {
    id: 'view',
    number: '06',
    railTitle: 'View & cadence',
    railSub: 'Board axis · sweep',
    gate: false,
  },
  {
    id: 'approvers',
    number: '07',
    railTitle: 'Approvers',
    railSub: 'Who clears gates',
    gate: false,
  },
  { id: 'activate', number: '08', railTitle: 'Activate', railSub: 'The one gate', gate: true },
];

/** Resolve a step by index, clamped to the rail (noUncheckedIndexedAccess-safe). */
export function composerStepAt(index: number): ComposerStep {
  return COMPOSER_STEPS[index] ?? BRIEF_STEP;
}

/** A step's main heading — the title + explanatory sub the build column renders. */
export interface ComposerStepHeading {
  readonly title: string;
  readonly sub: string;
}

const STEP_HEADINGS: Readonly<Record<ComposerStepId, ComposerStepHeading>> = {
  brief: {
    title: 'Describe the job',
    sub: 'Paste the owner’s ask in their own words. Ritona parses the intent and drafts every primitive — you review each one before it can run.',
  },
  skill: {
    title: 'Skill',
    sub: 'The durable intent Ritona works to. Versioned with the definition and rendered verbatim at the gate — the worker gets this, not the manager’s charter.',
  },
  bindings: {
    title: 'Bindings',
    sub: 'The declared pulls. The platform runs these, not the model — so the dispute email can never be quietly omitted.',
  },
  lifecycle: {
    title: 'Lifecycle',
    sub: 'The states an item moves through, and its two exits. Validated here: exactly one goal exit and one dead exit.',
  },
  actions: {
    title: 'Action palette',
    sub: 'Everything the workflow may do, each mapped to an owning module and capability. Amber means gated — anything that touches the world stops for a verdict.',
  },
  view: {
    title: 'View & cadence',
    sub: 'Ritona proposes the board axis that fits the shape, and the sweep cadence. Both are yours to override.',
  },
  approvers: {
    title: 'Approvers',
    sub: 'Who can clear this workflow’s gates. Every verdict is logged against them.',
  },
  activate: {
    title: 'Activate',
    sub: 'One gate, ever — because cadence is a field, not a Schedule. Review the real configuration, then activate.',
  },
};

/** A step's heading; the activation gate names the drafted workflow (prototype STEP_HEAD.activate). */
export function composerStepHeading(id: ComposerStepId, workflowName: string): ComposerStepHeading {
  const heading = STEP_HEADINGS[id];
  if (id !== 'activate') return heading;
  return { title: `Activate ${workflowName.toLowerCase()}`, sub: heading.sub };
}

/** The staged-reveal lines the drafting moment shows (prototype draftingBody), in reveal order. */
export const DRAFT_REVEAL_ROWS: readonly string[] = [
  'Reading the brief',
  'Selecting the skill',
  'Declaring the pulls',
  'Laying out the lifecycle',
  'Mapping the action palette',
  'Choosing the board view',
];

/** Reveal pacing: one row per tick, then a short hold before landing on the Skill step. */
export const DRAFT_REVEAL_TICK_MS = 130;
export const DRAFT_REVEAL_HOLD_MS = 320;

/** Render a lifecycle state key for display (e.g. `written_off` → `written off`). */
export function displayState(state: string): string {
  return state.replaceAll('_', ' ');
}

/** The one-line lifecycle summary the activation review renders (states + the two exits). */
export function lifecycleLine(lifecycle: {
  readonly states: readonly string[];
  readonly goalExit: string;
  readonly deadExit: string;
}): string {
  const flow = lifecycle.states.map(displayState).join(' → ');
  return `${flow} · ✓ ${displayState(lifecycle.goalExit)} · ⊘ ${displayState(lifecycle.deadExit)}`;
}
